/**
 * Фасад между ProfileState, контентом и rules.
 * Источник правды для контента: ContentLoader.loadContent() (content.json).
 * Hub/Pack/Collection view строятся по данным из content через ContentRepository.
 */

import { PACK_PUZZLE_DIFFICULTIES } from '../data/types';
import type { PackId, PuzzleId } from '../data/types';
import type { ActivePuzzleSource, ProfileState, TutorialStatus } from '../state/Profile';
import { deriveCollectedCount } from '../state/Profile';
import { load as loadProfile, save as saveProfile } from './ProfileStorage';
import {
  getAllPacks,
  getPuzzlesForPack,
  getPuzzleMeta,
  getPuzzleMetaByKey,
  pickDailyPuzzle,
  TUTORIAL_PUZZLE_ID,
} from './ContentRepository';
import { clearPuzzleState, loadPuzzleState } from './PuzzleStateStorage';
import {
  clearCollectionRebuildSession,
  clearPuzzleActionSession,
} from './CollectionRebuildStorage';
import { resolvePublicAssetPath } from '../utils/paths';
import {
  getAllowedDifficultiesForEntry,
  getNextDifficultyUnlockProgress,
} from './rules';
import { emit } from './EventBus';

export type DailyCardStatus = 'new' | 'collected' | 'expired' | 'inProgress';

export interface HubViewDailyCard {
  status: DailyCardStatus;
  title: string;
  dailyKey: string;
  /** URL для превью (full из content → resolvePublicAssetPath). */
  imageUrl: string;
  /** Прогресс по кусочкам: сколько поставлено на доску / всего (для отображения на хабе). */
  progressCollected: number;
  progressTotal: number;
}

export interface HubViewPackRow {
  packId: PackId;
  title: string;
  collectedInPack: number;
  total: number;
  isPurchased: boolean;
  /** URL превью первого пазла пака (ленивая загрузка). */
  firstPuzzleImageUrl: string;
  /** true, если в паке есть пазл за рекламу (2-й по счёту). Для бейджа "Ad"/"Reward" на карточке. */
  hasRewardedPuzzle: boolean;
}

export interface HubView {
  dailyCard: HubViewDailyCard;
  packs: HubViewPackRow[];
  coins: number;
  hasActivePuzzle: boolean;
  /** Источник активного пазла (для кнопки «Продолжить» в Daily). */
  activePuzzleSource: ActivePuzzleSource | null;
}

export type PackPuzzleStatus =
  | 'free'
  | 'rewardedLocked'
  | 'rewardedUnlocked'
  | 'locked'
  | 'availableOwned';

export interface PackPuzzleView {
  puzzleId: PuzzleId;
  title?: string;
  orderInPack: number;
  status: PackPuzzleStatus;
  imageUrl: string;
  /** Допустимые сложности для выбора (только у играбельных). */
  allowedDifficulties: number[];
}

export interface PackView {
  packId: PackId;
  title: string;
  puzzles: PackPuzzleView[];
  difficultyRange: string;
  lockedPromoText: string;
  isPurchased: boolean;
  collectedInPack: number;
  total: number;
}

export interface CollectionViewItem {
  key: string;
  imageUrl: string;
  title: string;
}

export interface CollectionView {
  collectedItems: CollectionViewItem[];
  unlockProgress: ReturnType<typeof getNextDifficultyUnlockProgress>;
  coins: number;
}

function getTodayDailyKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isPuzzleCollected(profile: ProfileState, puzzleId: PuzzleId, imagePath?: string): boolean {
  return (
    profile.collectedPuzzleImages[puzzleId] === true ||
    (!!imagePath && profile.collectedPuzzleImages[imagePath] === true)
  );
}

export function getHubView(): HubView {
  const profile = loadProfile();
  const todayKey = getTodayDailyKey();
  const dailyMeta = pickDailyPuzzle(todayKey);

  let dailyStatus: DailyCardStatus = 'new';
  if (profile.daily.collectedKey === todayKey) {
    dailyStatus = 'collected';
  } else if (
    profile.activePuzzle?.source === 'daily' &&
    profile.activePuzzle.puzzleId === dailyMeta.id
  ) {
    dailyStatus = 'inProgress';
  } else if (todayKey < getTodayDailyKey()) {
    dailyStatus = 'expired';
  }

  const isActiveDaily =
    profile.activePuzzle?.source === 'daily' && profile.activePuzzle.puzzleId === dailyMeta.id;
  const dailyDifficulty = isActiveDaily ? profile.activePuzzle!.difficulty : 45;
  let dailyProgressCollected = 0;
  let dailyProgressTotal = dailyDifficulty;
  if (isActiveDaily) {
    const state = loadPuzzleState(profile.activePuzzle!.puzzleId, profile.activePuzzle!.difficulty);
    if (state) {
      dailyProgressTotal = profile.activePuzzle!.difficulty;
      for (const entry of Object.values(state)) {
        if (entry.placedCorrect) dailyProgressCollected += 1;
      }
    }
  } else if (dailyStatus === 'collected') {
    dailyProgressCollected = dailyDifficulty;
  }

  const dailyCard: HubViewDailyCard = {
    status: dailyStatus,
    title: dailyMeta.title ?? 'Daily',
    dailyKey: todayKey,
    imageUrl: dailyMeta.imagePath ? resolvePublicAssetPath(dailyMeta.imagePath) : '',
    progressCollected: dailyProgressCollected,
    progressTotal: dailyProgressTotal,
  };

  const packs: HubViewPackRow[] = getAllPacks().map((pack) => {
    const puzzles = getPuzzlesForPack(pack.id);
    const first = puzzles[0];
    const collectedInPack = puzzles.filter((p) =>
      isPuzzleCollected(profile, p.id, p.imagePath)
    ).length;
    const hasRewardedPuzzle = puzzles.length >= 2;
    return {
      packId: pack.id,
      title: pack.title,
      collectedInPack,
      total: pack.puzzleIds.length,
      isPurchased: profile.purchasedPacks[pack.id] === true,
      firstPuzzleImageUrl: first?.imagePath ? resolvePublicAssetPath(first.imagePath) : '',
      hasRewardedPuzzle,
    };
  });

  return {
    dailyCard,
    packs,
    coins: profile.coins,
    hasActivePuzzle: !!profile.activePuzzle,
    activePuzzleSource: profile.activePuzzle?.source ?? null,
  };
}

export function getPackView(packId: PackId): PackView {
  const profile = loadProfile();
  const pack = getAllPacks().find((p) => p.id === packId);
  const puzzles = getPuzzlesForPack(packId);
  const isPurchased = profile.purchasedPacks[packId] === true;

  const allowed = getAllowedDifficultiesForEntry({
    source: 'pack',
    packOwned: isPurchased,
  });
  const difficultyRange =
    allowed.length > 0
      ? `${allowed[0]}–${allowed[allowed.length - 1]}`
      : '—';

  const freeRewardedDiffs = [...PACK_PUZZLE_DIFFICULTIES];
  const ownedDiffs = [...PACK_PUZZLE_DIFFICULTIES];

  const puzzlesView: PackPuzzleView[] = puzzles.map((p) => {
    let status: PackPuzzleStatus;
    const order = p.orderInPack ?? 0;
    if (order === 1) status = 'free';
    else if (order === 2)
      status = profile.rewardedUnlocked[p.id] === true ? 'rewardedUnlocked' : 'rewardedLocked';
    else status = isPurchased ? 'availableOwned' : 'locked';

    const allowedDifficulties =
      status === 'free' || status === 'rewardedLocked' || status === 'rewardedUnlocked'
        ? freeRewardedDiffs
        : status === 'availableOwned'
          ? ownedDiffs
          : [];

    return {
      puzzleId: p.id,
      title: p.title,
      orderInPack: order,
      status,
      imageUrl: p.imagePath ? resolvePublicAssetPath(p.imagePath) : '',
      allowedDifficulties,
    };
  });

  const collectedInPack = puzzles.filter((p) =>
    isPuzzleCollected(profile, p.id, p.imagePath)
  ).length;

  return {
    packId,
    title: pack?.title ?? packId,
    puzzles: puzzlesView,
    difficultyRange,
    lockedPromoText: 'Купите набор, чтобы открыть остальные пазлы.',
    isPurchased,
    collectedInPack,
    total: puzzles.length,
  };
}

export function getCollectionView(): CollectionView {
  const profile = loadProfile();
  const keys = Object.keys(profile.collectedPuzzleImages);
  const collectedAt = profile.collectedAt ?? {};
  const keysNewestFirst = [...keys].sort(
    (a, b) => (collectedAt[b] ?? 0) - (collectedAt[a] ?? 0)
  );
  const seenIds = new Set<PuzzleId>();
  const collectedItems: CollectionViewItem[] = [];
  for (const key of keysNewestFirst) {
    const meta = getPuzzleMetaByKey(key);
    if (!meta) continue;
    if (seenIds.has(meta.id)) continue;
    seenIds.add(meta.id);
    collectedItems.push({
      key: meta.id,
      imageUrl: meta.imagePath ? resolvePublicAssetPath(meta.imagePath) : '',
      title: meta.title ?? meta.id,
    });
  }
  return {
    collectedItems,
    unlockProgress: getNextDifficultyUnlockProgress(profile.collectedCount),
    coins: profile.coins ?? 0,
  };
}

export interface PuzzleViewActivePuzzle {
  sourceLabel: string;
  puzzleId: string;
  difficulty: number;
  startedAt: number;
}

export interface PuzzleView {
  activePuzzle: PuzzleViewActivePuzzle | null;
}

const SOURCE_LABELS: Record<ActivePuzzleSource, string> = {
  tutorial: 'Обучение',
  daily: 'Ежедневный',
  pack: 'Подборка',
  collection: 'Коллекция',
};

export function getPuzzleView(): PuzzleView {
  const profile = loadProfile();
  const ap = profile.activePuzzle;
  if (!ap) {
    return { activePuzzle: null };
  }
  return {
    activePuzzle: {
      sourceLabel: SOURCE_LABELS[ap.source],
      puzzleId: ap.puzzleId,
      difficulty: ap.difficulty,
      startedAt: ap.startedAt,
    },
  };
}

function persistAndEmit(profile: ProfileState): void {
  saveProfile(profile);
  emit('profileChanged');
}

export function startDaily(): void {
  const profile = loadProfile();
  const todayKey = getTodayDailyKey();
  const meta = pickDailyPuzzle(todayKey);
  profile.activePuzzle = {
    puzzleId: meta.id,
    source: 'daily',
    difficulty: 45,
    startedAt: Date.now(),
  };
  persistAndEmit(profile);
}

/** Запуск фиксированного пазла туториала (всегда study.png, id: tutorial). */
export function startTutorial(): void {
  const profile = loadProfile();
  profile.activePuzzle = {
    puzzleId: TUTORIAL_PUZZLE_ID,
    source: 'tutorial',
    difficulty: 16,
    startedAt: Date.now(),
  };
  persistAndEmit(profile);
}

export function startPackPuzzle(puzzleId: PuzzleId, difficulty: number): void {
  const profile = loadProfile();
  const meta = getPuzzleMeta(puzzleId);
  if (!meta || !meta.packId) return;
  profile.activePuzzle = {
    puzzleId,
    source: 'pack',
    difficulty: difficulty as import('../data/types').Difficulty,
    startedAt: Date.now(),
  };
  persistAndEmit(profile);
}

export function unlockRewarded(puzzleId: PuzzleId): void {
  const profile = loadProfile();
  profile.rewardedUnlocked[puzzleId] = true;
  persistAndEmit(profile);
}

export function purchasePack(packId: PackId): void {
  const profile = loadProfile();
  profile.purchasedPacks[packId] = true;
  persistAndEmit(profile);
}

export function clearActivePuzzle(): void {
  const profile = loadProfile();
  delete profile.activePuzzle;
  persistAndEmit(profile);
}

export function getTutorialStatus(): TutorialStatus {
  const profile = loadProfile();
  const s = profile.tutorialStatus;
  return (s === 'skipped' || s === 'completed' || s === 'step2' ? s : 'none') as TutorialStatus;
}

export function setTutorialStatus(status: TutorialStatus): void {
  const profile = loadProfile();
  profile.tutorialStatus = status;
  persistAndEmit(profile);
}

/** Добавить тестовый собранный пазл в коллекцию (debug:<timestamp>), пересчитать collectedCount. */
export function addDebugCollected(): void {
  const profile = loadProfile();
  const now = Date.now();
  const key = `debug:${now}`;
  profile.collectedPuzzleImages[key] = true;
  profile.collectedAt = profile.collectedAt ?? {};
  profile.collectedAt[key] = now;
  profile.collectedCount = deriveCollectedCount(profile);
  persistAndEmit(profile);
}

/** Очистить коллекцию: collectedPuzzleImages, collectedAt и collectedCount. */
export function clearCollection(): void {
  const profile = loadProfile();
  profile.collectedPuzzleImages = {};
  profile.collectedAt = {};
  profile.collectedCount = 0;
  persistAndEmit(profile);
}

/** Сложность при пересборе из коллекции: tutorial — 16, остальные — 24. */
export function getCollectionPuzzleDifficulty(key: string): number {
  if (key === TUTORIAL_PUZZLE_ID) return 16;
  return 24;
}

/** Начать пересбор из коллекции по ключу (imageKey или puzzleId). Сложность: 24. */
export function startCollectionPuzzle(key: string): void {
  const meta = getPuzzleMetaByKey(key);
  if (!meta) return;
  const difficulty = getCollectionPuzzleDifficulty(key);
  const profile = loadProfile();
  profile.activePuzzle = {
    puzzleId: meta.id,
    source: 'collection',
    difficulty: (difficulty === 16 ? 24 : difficulty) as import('../data/types').Difficulty,
    startedAt: Date.now(),
  };
  persistAndEmit(profile);
}

const COMPLETION_COINS_REWARD = 20;

/**
 * Завершить пазл: добавить в коллекцию (по puzzleId), начислить монеты, очистить activePuzzle и прогресс.
 * source: для daily выставляем daily.collectedKey, чтобы на главной было «Пазл собран» и «Пересобрать».
 */
export function completePuzzle(
  puzzleId: PuzzleId,
  difficulty: number,
  source?: ActivePuzzleSource
): void {
  const profile = loadProfile();
  const meta = getPuzzleMeta(puzzleId);
  const now = Date.now();
  profile.collectedPuzzleImages[puzzleId] = true;
  if (meta?.imagePath) profile.collectedPuzzleImages[meta.imagePath] = true;
  profile.collectedAt = profile.collectedAt ?? {};
  profile.collectedAt[puzzleId] = now;
  if (meta?.imagePath) profile.collectedAt[meta.imagePath] = now;
  profile.collectedCount = deriveCollectedCount(profile);
  profile.coins = (profile.coins || 0) + COMPLETION_COINS_REWARD;
  if (source === 'daily') {
    profile.daily = profile.daily ?? {};
    profile.daily.collectedKey = getTodayDailyKey();
  }
  if (source === 'pack') {
    clearPuzzleActionSession('pack', puzzleId);
  }
  if (source === 'collection' || source === 'tutorial') {
    clearCollectionRebuildSession(puzzleId);
  }
  delete profile.activePuzzle;
  persistAndEmit(profile);
  clearPuzzleState(puzzleId, difficulty);
}

/**
 * Следующий пазл в паке (для кнопки «Следующий пазл»). Возвращает null, если не из пака или это последний.
 */
export function getNextPuzzleInPack(currentPuzzleId: PuzzleId): { puzzleId: PuzzleId; packId: PackId } | null {
  const meta = getPuzzleMeta(currentPuzzleId);
  if (!meta?.packId) return null;
  const puzzles = getPuzzlesForPack(meta.packId);
  const idx = puzzles.findIndex((p) => p.id === currentPuzzleId);
  if (idx < 0 || idx >= puzzles.length - 1) return null;
  const next = puzzles[idx + 1];
  return { puzzleId: next.id, packId: meta.packId };
}
